import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CookieIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { onDiagnosticsConsentChanged } from "@/lib/errorReporter";
import { syncAnalyticsWithConsent } from "@/lib/analytics";
import api from "@/lib/api";

const KEY = "ap_cookie_consent_v1";

// Best-effort DPDP audit sync: if the visitor is logged in, mirror the
// banner choices to /user/consent so the audit log records them. Guests
// keep the choice in localStorage only — we sync on their next login.
async function syncConsentToServer(diagnostics, analytics) {
  try {
    await api.put(
      "/user/consent?source=banner",
      { consent_diagnostics: !!diagnostics, consent_analytics: !!analytics }
    );
  } catch {
    // Silent: guests are unauth (401) — that's expected; we sync at login.
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [diagnostics, setDiagnostics] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      setVisible(true);
      return;
    }
    // Honour any previously stored analytics choice on every boot.
    syncAnalyticsWithConsent();
    // If the visitor is logged in, mirror the banner choice to server-side
    // consent so the DPDP audit log stays in sync (idempotent).
    try {
      const parsed = JSON.parse(raw);
      syncConsentToServer(parsed?.diagnostics, parsed?.analytics);
    } catch {
      // Ignore malformed storage.
    }
  }, []);

  const save = (withDiagnostics, withAnalytics) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        essential: true,
        diagnostics: !!withDiagnostics,
        analytics: !!withAnalytics,
        at: new Date().toISOString(),
      })
    );
    onDiagnosticsConsentChanged();
    syncAnalyticsWithConsent();
    syncConsentToServer(withDiagnostics, withAnalytics);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50 rounded-lg border border-border bg-white shadow-lg p-5"
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <CookieIcon size={16} className="text-primary" />
          </div>
          <div>
            <div className="font-serif text-lg text-primary leading-tight">Your privacy, your choice</div>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Session cookies are always on. Everything else is opt-in and you can change it any time. See our{" "}
              <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
        <button
          onClick={() => setVisible(false)}
          className="text-muted-foreground hover:text-primary transition-colors"
          aria-label="Dismiss"
          data-testid="cookie-dismiss"
        >
          <X size={16} />
        </button>
      </div>
      <div className="mt-4 space-y-3 text-sm">
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
          <div>
            <div className="text-primary font-medium">Essential</div>
            <div className="text-[11px] text-muted-foreground">Session cookies · required</div>
          </div>
          <span className="text-[11px] text-muted-foreground">Always on</span>
        </div>
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
          <div>
            <div className="text-primary font-medium">Diagnostics</div>
            <div className="text-[11px] text-muted-foreground">First-party error reports · no third parties</div>
          </div>
          <Switch
            checked={diagnostics}
            onCheckedChange={setDiagnostics}
            data-testid="cookie-diagnostics-toggle"
          />
        </div>
        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
          <div>
            <div className="text-primary font-medium">Product analytics</div>
            <div className="text-[11px] text-muted-foreground">Usage patterns + session replay via PostHog</div>
          </div>
          <Switch
            checked={analytics}
            onCheckedChange={setAnalytics}
            data-testid="cookie-analytics-toggle"
          />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => save(false, false)}
          data-testid="cookie-essentials-only"
        >
          Essentials only
        </Button>
        <Button className="flex-1" onClick={() => save(diagnostics, analytics)} data-testid="cookie-save">
          Save choices
        </Button>
      </div>
    </div>
  );
}
